import * as React from 'react';
import { textDriverFactory } from './Text.driver';
import { Text, TextTypography, TextPriority } from '.';
import {
  TPAComponentsWrapper,
  createRendererWithUniDriver,
} from '../../test/utils';

describe('Text', () => {
  const render = createRendererWithUniDriver(textDriverFactory);

  it('should render', async () => {
    const value = 'hello!';

    const { driver } = render(<Text>{value}</Text>);

    expect(await driver.getContent()).toEqual(value);
  });

  it('should render smallTitle', async () => {
    const expectedTag = 'h5';
    const expectedTypography = TextTypography.smallTitle;

    const { driver } = render(
      <Text tagName={expectedTag} typography={expectedTypography} />,
    );

    expect(await driver.getTagName()).toEqual(expectedTag);
    expect(await driver.getTypography()).toEqual(expectedTypography);
  });

  it('should set lang on root', async () => {
    const lang = 'he';
    const { driver } = render(<Text lang={lang} />);

    expect(await driver.getLang()).toEqual(lang);
  });

  it('should render runningText', async () => {
    const expectedTag = 'p';
    const expectedTypography = TextTypography.runningText;

    const { driver } = render(
      <Text tagName={expectedTag} typography={expectedTypography} />,
    );

    expect(await driver.getTagName()).toEqual(expectedTag);
    expect(await driver.getTypography()).toEqual(expectedTypography);
  });

  it('should render runningText with secondary priority', async () => {
    const expectedTag = 'p';
    const expectedTypography = TextTypography.runningText;
    const expectedPriority = TextPriority.secondary;

    const { driver } = render(
      <Text
        tagName={expectedTag}
        typography={expectedTypography}
        priority={expectedPriority}
      />,
    );

    expect(await driver.getTagName()).toEqual(expectedTag);
    expect(await driver.getTypography()).toEqual(expectedTypography);
    expect(await driver.getPriority()).toEqual(expectedPriority);
  });

  it('should render listText', async () => {
    const expectedTag = 'p';
    const expectedTypography = TextTypography.listText;

    const { driver } = render(
      <Text tagName={expectedTag} typography={expectedTypography} />,
    );

    expect(await driver.getTagName()).toEqual(expectedTag);
    expect(await driver.getTypography()).toEqual(expectedTypography);
  });

  it('should render largeTitle', async () => {
    const expectedTag = 'p';
    const expectedTypography = TextTypography.largeTitle;

    const { driver } = render(
      <Text tagName={expectedTag} typography={expectedTypography} />,
    );

    expect(await driver.getTagName()).toEqual(expectedTag);
    expect(await driver.getTypography()).toEqual(expectedTypography);
  });

  it('should use default props', async () => {
    const expectedTag = 'span';
    const expectedTypography = TextTypography.runningText;

    const { driver } = render(<Text />);

    expect(await driver.getTagName()).toEqual(expectedTag);
    expect(await driver.getTypography()).toEqual(expectedTypography);
    expect(await driver.isMobile()).toEqual(false);
  });

  it('should use mobile design', async () => {
    const { driver } = render(
      TPAComponentsWrapper({ mobile: true })(<Text data-hook="text" />),
      'text',
    );

    expect(await driver.isMobile()).toEqual(true);
  });

  describe.skip('aria-label', () => {
    it('should render a sibling element for the aria label', async () => {
      const value = 'hello!';
      const ariaLabelValue = 'aria label ' + value;

      const { driver } = render(
        <Text aria-label={ariaLabelValue} aria-hidden={false}>
          {value}
        </Text>,
      );

      expect(await driver.isAriaLabelElementExists()).toBe(true);
      expect(await driver.getAriaLabelContent()).toEqual(ariaLabelValue);
      expect(await driver.getAttribute('aria-label')).toEqual(ariaLabelValue);
    });

    it('should mark the original text element as aria-hidden when aria label is provided', async () => {
      const value = 'hello!';
      const ariaLabelValue = 'aria label ' + value;

      const { driver } = render(
        <Text aria-label={ariaLabelValue} aria-hidden="false">
          {value}
        </Text>,
      );

      expect(await driver.getAttribute('aria-hidden')).toBe('true');
    });

    it('should not generate an aria label element when aria label is not provided', async () => {
      const value = 'hello!';
      const ariaLabelValue = undefined;

      const { driver } = render(
        <Text aria-label={ariaLabelValue} aria-hidden="false">
          {value}
        </Text>,
      );

      expect(await driver.getAttribute('aria-hidden')).toBe('false');
      expect(await driver.isAriaLabelElementExists()).toBe(false);
    });

    it('should not generate an aria label element when the text element is already marked as aria-hidden', async () => {
      const value = 'hello!';
      const ariaLabelValue = 'aria label ' + value;

      const { driver } = render(
        <Text aria-label={ariaLabelValue} aria-hidden="true">
          {value}
        </Text>,
      );

      expect(await driver.isAriaLabelElementExists()).toBe(false);
    });

    it('should mark the original element as aria-hidden when defined as such in the component props', async () => {
      const value = 'hello!';
      const ariaLabelValue = 'aria label ' + value;

      const { driver } = render(
        <Text aria-label={ariaLabelValue} aria-hidden>
          {value}
        </Text>,
      );

      expect(await driver.isAriaLabelElementExists()).toBe(false);
      expect(await driver.getAttribute('aria-hidden')).toBe('true');
    });
  });
});
